import { unwrapApiResponse } from "../utils/apiUtils";
import { api } from "./apiSlice";
import { PaginatedList } from "../types/pagination";

export interface MaintenanceAlertDto {
  id: number;
  vehicleId: number;
  vehicleName: string;
  alertType: string;
  severity: string;
  message: string;
  isActive: boolean;
  createdAt: string;
  resolvedAt: string | null;
  currentValue: number | null;
  thresholdValue: number | null;
}

export interface TireChangeDto {
  position: string;
  date: string | null;
  note: string | null;
  treadDepth32nds: number | null;
  brakePadMm: number | null;
}

export interface VehicleMaintenanceDto {
  vehicleId: number;
  vehicleName: string;
  currentOdometer: number | null;

  lastOilChange: number | null;
  oilChangeThreshold: number | null;
  lastOilChangeDate: string | null;
  nowOil: number | null;

  alignmentDate: string | null;

  tiresChanged: TireChangeDto[];

  lastTuneUp: number | null;
  tuneUpDate: string | null;
  nowTuneUp: number | null;

  dpfFilterClean: number | null;
  dpfFilterCleanDate: string | null;
  nowDpfFilter: number | null;
  isDpfRemoved: boolean;

  defChange: string | null;

  airDryFilterChange: number | null;
  airDryFilterChangeDate: string | null;
  nowAirDryFilter: number | null;

  winterDate: string | null;

  motorkote: string | null;

  lastCoolantChange: number | null;
  lastCoolantChangeDate: string | null;
  nowCoolant: number | null;

  emissionExpDate: string | null;

  activeAlerts: MaintenanceAlertDto[];
}

export interface MaintenanceInvoiceDto {
  id: number;
  originalFileName: string;
  url: string;
  contentType: string;
  fileSizeBytes: number;
  uploadedAt: string;
}

export interface MaintenanceRecordDto {
  id: number;
  vehicleId: number;
  vehicleName: string;
  shopId: number | null;
  shopName: string | null;
  cost: number | null;
  date: string;
  mileage: number | null;
  description: string;
  invoices: MaintenanceInvoiceDto[];
  createdAt: string;
}

export interface TrailerMaintenanceRecordDto {
  id: number;
  trailerId: number;
  trailerName: string;
  shopId: number | null;
  shopName: string | null;
  cost: number | null;
  date: string;
  mileage: number | null;
  description: string;
  invoices: MaintenanceInvoiceDto[];
  createdAt: string;
}

export interface CreateMaintenanceRecordRequest {
  vehicleId: number;
  shopId: number | null;
  cost: number | null;
  date: string;
  mileage: number | null;
  description: string;
  invoiceIds: number[] | null;
}

export interface UpdateMaintenanceRecordRequest {
  vehicleId: number;
  shopId: number | null;
  cost: number | null;
  date: string;
  mileage: number | null;
  description: string;
  invoiceIds: number[] | null;
}

export interface CreateTrailerMaintenanceRecordRequest {
  trailerId: number;
  shopId: number | null;
  cost: number | null;
  date: string;
  mileage: number | null;
  description: string;
  invoiceIds: number[] | null;
}

export interface UpdateTrailerMaintenanceRecordRequest {
  trailerId: number;
  shopId: number | null;
  cost: number | null;
  date: string;
  mileage: number | null;
  description: string;
  invoiceIds: number[] | null;
}

export interface UpdateVehicleMaintenanceRequest {
  lastOilChange: number | null;
  oilChangeThreshold: number | null;
  lastOilChangeDate: string | null;
  alignmentDate: string | null;
  tiresChanged: TireChangeDto[] | null;
  lastTuneUp: number | null;
  tuneUpDate: string | null;
  dpfFilterClean: number | null;
  dpfFilterCleanDate: string | null;
  isDpfRemoved: boolean;
  defChange: string | null;
  airDryFilterChange: number | null;
  airDryFilterChangeDate: string | null;
  winterDate: string | null;
  motorkote: string | null;
  lastCoolantChange: number | null;
  lastCoolantChangeDate: string | null;
  emissionExpDate: string | null;
}

export interface MaintenanceInvoiceQueryArgs {
  vehicleId?: number;
  trailerId?: number;
  pageNumber?: number;
  pageSize?: number;
  search?: string;
}

export const maintenanceApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getMaintenanceData: builder.query<VehicleMaintenanceDto[], void>({
      query: () => "maintenance",
      transformResponse: unwrapApiResponse,
      providesTags: ["Maintenance"],
    }),
    getMaintenanceAlerts: builder.query<MaintenanceAlertDto[], void>({
      query: () => "maintenance/alerts",
      transformResponse: unwrapApiResponse,
      providesTags: ["Maintenance"],
    }),
    getMaintenanceAlertHistory: builder.query<MaintenanceAlertDto[], void>({
      query: () => "maintenance/alerts/history",
      transformResponse: unwrapApiResponse,
      providesTags: ["Maintenance"],
    }),
    getVehicleMaintenance: builder.query<VehicleMaintenanceDto, number>({
      query: (vehicleId) => `maintenance/${vehicleId}`,
      transformResponse: unwrapApiResponse,
      providesTags: (_result, _error, vehicleId) => [
        { type: "Maintenance", id: vehicleId },
      ],
    }),
    updateVehicleMaintenance: builder.mutation<
      VehicleMaintenanceDto,
      { vehicleId: number; data: UpdateVehicleMaintenanceRequest }
    >({
      query: ({ vehicleId, data }) => ({
        url: `maintenance/${vehicleId}`,
        method: "PUT",
        body: data,
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: ["Maintenance"],
    }),

    getMaintenanceRecords: builder.query<
      MaintenanceRecordDto[],
      number | void
    >({
      query: (vehicleId) =>
        vehicleId
          ? `maintenance/records?vehicleId=${vehicleId}`
          : "maintenance/records",
      transformResponse: unwrapApiResponse,
      providesTags: ["MaintenanceRecords"],
    }),
    createMaintenanceRecord: builder.mutation<
      MaintenanceRecordDto,
      CreateMaintenanceRecordRequest
    >({
      query: (data) => ({
        url: "maintenance/records",
        method: "POST",
        body: data,
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: ["MaintenanceRecords", "MaintenanceInvoices"],
    }),
    updateMaintenanceRecord: builder.mutation<
      MaintenanceRecordDto,
      { recordId: number; data: UpdateMaintenanceRecordRequest }
    >({
      query: ({ recordId, data }) => ({
        url: `maintenance/records/${recordId}`,
        method: "PUT",
        body: data,
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: ["MaintenanceRecords", "MaintenanceInvoices"],
    }),
    deleteMaintenanceRecord: builder.mutation<void, number>({
      query: (recordId) => ({
        url: `maintenance/records/${recordId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["MaintenanceRecords"],
    }),

    getTrailerMaintenanceRecords: builder.query<
      TrailerMaintenanceRecordDto[],
      number | void
    >({
      query: (trailerId) =>
        trailerId
          ? `maintenance/trailer-records?trailerId=${trailerId}`
          : "maintenance/trailer-records",
      transformResponse: unwrapApiResponse,
      providesTags: ["TrailerMaintenanceRecords"],
    }),
    createTrailerMaintenanceRecord: builder.mutation<
      TrailerMaintenanceRecordDto,
      CreateTrailerMaintenanceRecordRequest
    >({
      query: (data) => ({
        url: "maintenance/trailer-records",
        method: "POST",
        body: data,
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: ["TrailerMaintenanceRecords", "MaintenanceInvoices"],
    }),
    updateTrailerMaintenanceRecord: builder.mutation<
      TrailerMaintenanceRecordDto,
      { recordId: number; data: UpdateTrailerMaintenanceRecordRequest }
    >({
      query: ({ recordId, data }) => ({
        url: `maintenance/trailer-records/${recordId}`,
        method: "PUT",
        body: data,
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: ["TrailerMaintenanceRecords", "MaintenanceInvoices"],
    }),
    deleteTrailerMaintenanceRecord: builder.mutation<void, number>({
      query: (recordId) => ({
        url: `maintenance/trailer-records/${recordId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["TrailerMaintenanceRecords"],
    }),

    getMaintenanceInvoices: builder.query<
      PaginatedList<MaintenanceInvoiceDto>,
      MaintenanceInvoiceQueryArgs
    >({
      query: ({ vehicleId, trailerId, pageNumber = 1, pageSize = 24, search }) => {
        const base = vehicleId
          ? `maintenance/vehicles/${vehicleId}/invoices`
          : `maintenance/trailers/${trailerId}/invoices`;
        const params = new URLSearchParams({
          pageNumber: String(pageNumber),
          pageSize: String(pageSize),
        });
        if (search) params.set("search", search);
        return `${base}?${params.toString()}`;
      },
      transformResponse: unwrapApiResponse,
      providesTags: ["MaintenanceInvoices"],
    }),
    uploadMaintenanceInvoice: builder.mutation<
      MaintenanceInvoiceDto,
      { vehicleId?: number; trailerId?: number; formData: FormData }
    >({
      query: ({ vehicleId, trailerId, formData }) => ({
        url: vehicleId
          ? `maintenance/vehicles/${vehicleId}/invoices/upload`
          : `maintenance/trailers/${trailerId}/invoices/upload`,
        method: "POST",
        body: formData,
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: ["MaintenanceInvoices"],
    }),
    deleteMaintenanceInvoice: builder.mutation<void, number>({
      query: (invoiceId) => ({
        url: `maintenance/invoices/${invoiceId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["MaintenanceInvoices"],
    }),
  }),
});

export const {
  useGetMaintenanceDataQuery,
  useGetMaintenanceAlertsQuery,
  useGetMaintenanceAlertHistoryQuery,
  useGetVehicleMaintenanceQuery,
  useUpdateVehicleMaintenanceMutation,
  useGetMaintenanceRecordsQuery,
  useCreateMaintenanceRecordMutation,
  useUpdateMaintenanceRecordMutation,
  useDeleteMaintenanceRecordMutation,
  useGetTrailerMaintenanceRecordsQuery,
  useCreateTrailerMaintenanceRecordMutation,
  useUpdateTrailerMaintenanceRecordMutation,
  useDeleteTrailerMaintenanceRecordMutation,
  useGetMaintenanceInvoicesQuery,
  useUploadMaintenanceInvoiceMutation,
  useDeleteMaintenanceInvoiceMutation,
} = maintenanceApi;
